import { useMemo, useState, useEffect, useRef } from 'react'
import { AppTitlePage } from '../../components/AppTitlePage'
import { AppBtnColor } from '../../components/btn/AppBtn'
import { AppFilterDateInput } from '../../components/AppInput/AppFilterDateInput'
import { useGetBuffetDishSimpleListQuery } from '../../core/api/BuffetDishApi'
import { AppFilterMultiDropdownWithPagination } from '../../components/AppDropdown/AppFilterMultiDropdownWithPagination'
import { IBaseSimpleList } from '../../core/api/dto/BaseDto'
import Checkbox from '../../components/checkbox/Checkbox'
import { useParamsControl } from '../../utils/useParamsControl'
import { AppFilterDropdown } from '../../components/AppDropdown/AppFilterDropdown'
import { IPlanMenuTypeDto } from '../../core/api/dto/PlanMenuTypeDto'
import { useGetPlanMenuTypeListQuery } from '../../core/api/PlanMenuTypeApi'
import { useSearchParams } from 'react-router-dom'
import { useGetCategorySimpleListQuery } from '../../core/api/CategoryApi'
import { useNavigate } from 'react-router'
import { AllRoutes } from '../../core/routes/AllRoutes'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
// import { useGetStationsQuery } from 'core/api/StationsApi'
import { IBuffetGeneralReportRequest } from 'core/api/dto/BuffetGeneralReportDto'
import './BuffetReportsVZ.scss'
import {
  DimensionTranslateType,
  DimensionType,
} from 'core/api/dto/BuffetReportDto'
import { AppFilterTimeInput } from 'components/AppInput/AppFilterTimeInput'
import { AppFilterMultiDropdown } from 'components/AppDropdown/AppFilterMultiDropdown'
import { dimensionTypeData } from '../PlanMenuControl/PlanMenuControlAdmin'
import { Lang } from 'components/keyboardWrapper/KeyboardWrapper'
import { useGetBiAnalyticsUrlQuery } from 'core/api/BuffetReportApi'
import { useGetTagsListQuery } from 'core/api/TagsApi'
import { useGetPlanMenuSimpleListQuery } from 'core/api/PlanMenuApi'
import { useGetValueTypeDimensionListQuery } from 'core/api/ValueTypeDimensionApi'
import { IValueTypeDimensionDto } from 'core/api/dto/ValueTypeDimensionDto'

export enum ShowFormatType {
  'full' = 'full',
  'visual' = 'visual',
  'intensity' = 'intensity',
  'temperature' = 'temperature',
  'volume' = 'volume',
  'bi' = 'bi',
}

interface ShowFormatItem {
  searchParam: string
  value: ShowFormatType
  name: string
}

interface IDimensionTypeData {
  name: DimensionTranslateType | DimensionType
  value: DimensionType
}

const baseShowFormatData: ShowFormatItem[] = [
  {
    searchParam: 'report_type',
    value: ShowFormatType.full,
    name: 'По всем данным',
  },
  {
    searchParam: 'report_type',
    value: ShowFormatType.volume,
    name: 'Объем выдаваемой продукции',
  },
  {
    searchParam: 'report_type',
    value: ShowFormatType.temperature,
    name: 'Температурный',
  },
  {
    searchParam: 'report_type',
    value: ShowFormatType.visual,
    name: 'Визуальный',
  },
  {
    searchParam: 'report_type',
    value: ShowFormatType.intensity,
    name: 'Интенсивность выдаваемой продукции',
  },
]

const timeIntervalData = [
  {
    name: `${
      process.env.REACT_APP_LANG === Lang.RU ? '15 минут' : '15 minutes'
    }`,
    value: 15,
  },
  {
    name: `${
      process.env.REACT_APP_LANG === Lang.RU ? '30 минут' : '15 minutes'
    }`,
    value: 30,
  },
  {
    name: `${process.env.REACT_APP_LANG === Lang.RU ? '1 час' : '1 hour'}`,
    value: 60,
  },
  {
    name: `${process.env.REACT_APP_LANG === Lang.RU ? '3 часa' : '3 hours'}`,
    value: 180,
  },
]

const dishReplacementData = [
  {
    name: `${
      process.env.REACT_APP_LANG === Lang.RU
        ? 'Замеры у которых были замены'
        : 'Measurements that had replacements'
    }`,
    value: true,
  },
  {
    name: `${
      process.env.REACT_APP_LANG === Lang.RU
        ? 'Замеры у которых не было замены'
        : 'Measurements that had no replacements'
    }`,
    value: false,
  },
]

const standardData = [
  {
    name: `${
      process.env.REACT_APP_LANG === Lang.RU ? 'Обработка' : 'Processing'
    }`,
    value: 'Processing',
  },
  {
    name: `${
      process.env.REACT_APP_LANG === Lang.RU
        ? 'Соответствует'
        : 'Matches standard'
    }`,
    value: 'Compliant',
  },
  {
    name: `${
      process.env.REACT_APP_LANG === Lang.RU
        ? 'Не соответствует'
        : 'Does not match standard'
    }`,
    value: 'Non_compliant',
  },
  {
    name: `${
      process.env.REACT_APP_LANG === Lang.RU ? 'Не размечено' : 'Not assigned'
    }`,
    value: 'Not_determined',
  },
  {
    name: `${
      process.env.REACT_APP_LANG === Lang.RU
        ? 'Отсутствует модель'
        : 'Model missing'
    }`,
    value: 'Absent_model',
  },
]

export const BuffetReportsVZ = () => {
  const { t } = useTranslation()
  const pageParams = useParamsControl<
    IBuffetGeneralReportRequest,
    keyof IBuffetGeneralReportRequest
  >({
    withPagination: true,
    whiteList: [
      'report_type',
      'dimension_date_gte',
      'dimension_date_lte',
      'dimension_types',
      'dish_ids',
      'plan_menu_type_ids',
      'dimension_time_gte',
      'dimension_time_lte',
      'internal_dish_category_ids',
      'is_dish_replaced',
      'compliant_type',
      'tag_ids',
      'plan_menu_ids',
      'value_type_dimension',
    ],
  })
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const { data: biData } = useGetBiAnalyticsUrlQuery()
  const showBiAvailable = !!biData?.showInReport

  const showFormatData = useMemo<ShowFormatItem[]>(() => {
    const arr = [...baseShowFormatData]
    if (showBiAvailable) {
      arr.push({
        searchParam: '',
        value: ShowFormatType.bi,
        name: 'BI аналитика',
      })
    }
    return arr
  }, [showBiAvailable])

  const [showFormat, setShowFormat] = useState(
    () =>
      showFormatData.find(
        (item) =>
          item.value === (searchParams.get('report_type') as ShowFormatType),
      ) || showFormatData[0],
  )

  useEffect(() => {
    if (!showFormatData.find((i) => i.value === showFormat.value)) {
      setShowFormat(showFormatData[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFormatData])
  const disabledApproveFilter = false

  const onApproveFilter = () => {
    searchParams.set('report_type', showFormat.value)
    if (!searchParams.get('dimension_date_gte')) {
      searchParams.set('dimension_date_gte', moment().format('YYYY-MM-DD'))
    }
    if (!searchParams.get('dimension_date_lte')) {
      searchParams.set('dimension_date_lte', moment().format('YYYY-MM-DD'))
    }
    navigate(
      `${AllRoutes.buffetReportsVZLine.path}/?${searchParams.toString()}`,
    )
  }

  const clearFilterHandler = () => {
    setShowFormat(showFormatData[0])
    setSearchParams('')
  }

  const actions = useMemo(
    () => [
      {
        btnTitle: 'Применить фильтр',
        color: AppBtnColor.violetWhite,
        onClick: onApproveFilter,
        disabled: disabledApproveFilter,
      },
      {
        btnTitle: 'Сбросить фильтр',
        color: AppBtnColor.extraVioletViolet,
        onClick: clearFilterHandler,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      disabledApproveFilter,
      pageParams,
      showFormat,
      clearFilterHandler,
      onApproveFilter,
    ],
  )

  const onShowFormatHandler = (item: ShowFormatItem) => () => {
    if (item.value === ShowFormatType.bi) {
      navigate(`${AllRoutes.buffetReportsBi.path}`)
      return
    }
    searchParams.set('report_type', item.value)
    setSearchParams(searchParams.toString())
    setShowFormat(item)
  }

  const dimensionTypesKey = searchParams.getAll('dimension_types').join(',')
  const dimensionTypesKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (dimensionTypesKeyRef.current === null) {
      dimensionTypesKeyRef.current = dimensionTypesKey
      return
    }
    if (dimensionTypesKeyRef.current === dimensionTypesKey) return
    dimensionTypesKeyRef.current = dimensionTypesKey
    if (!searchParams.has('value_type_dimension')) return
    searchParams.delete('value_type_dimension')
    setSearchParams(searchParams.toString(), { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionTypesKey])

  const currentReportType =
    (pageParams?.report_type as ShowFormatType) || showFormat.value
  return (
    <div className="width-for-page-wrapper general-report-wrap">
      <AppTitlePage title="Отчёты" actions={actions} />
      <div className="filter-block mt-20">
        <div>
          <label>
            {t('Дата')}
            <span className="color-Red"> *</span>
          </label>
          <div className="input-row mt-10">
            <AppFilterDateInput
              searchParam={'dimension_date_gte'}
              maxValue={
                pageParams?.dimension_date_lte || moment().format('MM.DD.YYYY')
              }
            />
            <AppFilterDateInput
              searchParam={'dimension_date_lte'}
              maxValue={moment().format('MM.DD.YYYY')}
              minValue={pageParams?.dimension_date_gte}
            />
          </div>
        </div>
        <div className="time-inputs-wrap">
          <label>{t('Время')}</label>
          <div className="input-row mt-10">
            <AppFilterTimeInput searchParam={'dimension_time_gte'} />
            <AppFilterTimeInput searchParam={'dimension_time_lte'} />
          </div>
        </div>
      </div>
      <div className="radio-filter-block mt-20">
        <span className="text">{t('Название отчета')}</span>
        <div className="radio-list mt-20">
          {showFormatData.map((item) => (
            <Checkbox
              name={item.value}
              key={item.value}
              value={item.value === showFormat.value}
              text={t(item.name)}
              isRadioView
              onChange={onShowFormatHandler(item)}
            />
          ))}
        </div>
      </div>

      <div className="comparative-filter-block">
        <span className="title">{t('Выбрать фильтр')}</span>

        <AppFilterMultiDropdownWithPagination<
          IBaseSimpleList,
          keyof IBaseSimpleList
        >
          searchParam={'dish_ids'}
          propToShowInList={'name'}
          valuePropName={'id'}
          label={'Названия блюд'}
          placeholder={'Выбрать'}
          fullWidth
          getterData={useGetBuffetDishSimpleListQuery}
          limit={20}
          apiFilterParam={'ids'}
        />
        <AppFilterMultiDropdown<IDimensionTypeData, keyof IDimensionTypeData>
          searchParam={'dimension_types'}
          data={dimensionTypeData}
          propToShowInList={'name'}
          valuePropName={'value'}
          label={'Тип замера'}
          placeholder={'Выбрать'}
        />
        <AppFilterMultiDropdownWithPagination<
          IValueTypeDimensionDto,
          keyof IValueTypeDimensionDto
        >
          searchParam={'value_type_dimension'}
          getterData={useGetValueTypeDimensionListQuery}
          propToShowInList={'name'}
          valuePropName={'id'}
          label={'Значение типа замера'}
          placeholder={pageParams?.dimension_types ? 'Выбрать' : 'Сначала выберите тип замера'}
          fullWidth
          limit={20}
          apiFilterParam={'ids'}
          disabled={!pageParams?.dimension_types}
          extraOptions={
            pageParams?.dimension_types
              ? {
                  dimension_type: Array.isArray(pageParams.dimension_types)
                    ? pageParams.dimension_types
                    : String(pageParams.dimension_types).split(','),
                }
              : undefined
          }
        />
        <AppFilterMultiDropdownWithPagination<
          IPlanMenuTypeDto,
          keyof IPlanMenuTypeDto
        >
          searchParam={'plan_menu_type_ids'}
          getterData={useGetPlanMenuTypeListQuery}
          propToShowInList={'name'}
          valuePropName={'id'}
          label={'Тип план-меню'}
          placeholder={'Выбрать'}
          fullWidth
          limit={20}
          apiFilterParam={'ids'}
        />
        <AppFilterMultiDropdownWithPagination<
          IPlanMenuTypeDto,
          keyof IPlanMenuTypeDto
        >
          searchParam={'plan_menu_ids'}
          getterData={useGetPlanMenuSimpleListQuery}
          propToShowInList={'name'}
          valuePropName={'id'}
          label={'План-меню'}
          placeholder={'Выбрать'}
          fullWidth
          limit={20}
          apiFilterParam={'ids'}
        />
        {currentReportType === ShowFormatType.intensity && (
          <AppFilterDropdown
            searchParam={'interval_minutes'}
            data={timeIntervalData}
            valuePropName={'value'}
            propToShowInList={'name'}
            label={'Интервал времени'}
            placeholder={`15 ${t('минут')}`}
            minHeight
            fullWidth
          />
        )}
        <AppFilterMultiDropdownWithPagination<
          IBaseSimpleList,
          keyof IBaseSimpleList
        >
          searchParam={'internal_dish_category_ids'}
          propToShowInList={'name'}
          valuePropName={'id'}
          label={'Категория'}
          placeholder={'Выбрать'}
          fullWidth
          getterData={useGetCategorySimpleListQuery}
          limit={20}
          apiFilterParam={'ids'}
        />
        <AppFilterDropdown
          searchParam={'is_dish_replaced'}
          data={dishReplacementData}
          valuePropName={'value'}
          propToShowInList={'name'}
          label={'Замена блюда'}
          placeholder={`Выбрать`}
          minHeight
          fullWidth
        />
        {(currentReportType === ShowFormatType.full ||
          currentReportType === ShowFormatType.visual) && (
          <AppFilterMultiDropdown
            searchParam={'compliant_type'}
            data={standardData}
            valuePropName={'value'}
            propToShowInList={'name'}
            label={'Соответствие эталонности'}
            placeholder={`Выбрать`}
          />
        )}
        <AppFilterMultiDropdownWithPagination<
          IBaseSimpleList,
          keyof IBaseSimpleList
        >
          searchParam={'tag_ids'}
          propToShowInList={'name'}
          valuePropName={'id'}
          label={'Хэштеги'}
          placeholder={'Выбрать'}
          fullWidth
          getterData={useGetTagsListQuery}
          limit={20}
          apiFilterParam={'ids'}
        />
      </div>
    </div>
  )
}
